import React, { useEffect, useState } from 'react';
import './CompanyInfoPage.css';
import { API_CONFIG } from '../config/api';

const API = API_CONFIG.ENDPOINTS.COMPANY_INFO;

export default function CompanyInfoPage() {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFooter, setPdfFooter] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [navbarLogoUrl, setNavbarLogoUrl] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [workOrderEditEnabled, setWorkOrderEditEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(API, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Greška čitanja.');
        const data = await res.json();
        if (cancelled) return;
        setName(data.name || '');
        setDescription(data.description || '');
        setPdfFooter(data.pdf_footer || '');
        setLogoUrl(data.logo_url || null);
        setNavbarLogoUrl(data.navbar_logo_url || null);
        setCertificateUrl(data.certificate_url || null);
        setRegistrationEnabled(!!data.registration_enabled);
        setWorkOrderEditEnabled(!!data.work_order_edit_enabled);
        setError('');
      } catch (e) {
        if (!cancelled) setError('Neuspješno učitavanje podataka.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  async function handleSave(e) {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await fetch(API, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, description, pdf_footer: pdfFooter, registration_enabled: registrationEnabled, work_order_edit_enabled: workOrderEditEnabled })
      });
      if (!res.ok) throw new Error('Greška spremanja.');
      await res.json();
      setError('');
      setSuccess('Uspješno spremljeno.');
    } catch (e) {
      setSuccess('');
      setError('Neuspješno spremanje.');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('logo', file);
    try {
      setUploading(true);
      const res = await fetch(`${API}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      if (!res.ok) throw new Error('Greška uploada.');
      const data = await res.json();
      setLogoUrl(data.logo_url);
      setError('');
      setSuccess('Logo je uspješno ažuriran.');
      
      // Notify navbar to refresh logo
      window.dispatchEvent(new CustomEvent('companyLogoUpdated'));
    } catch (e) {
      setSuccess('');
      setError('Neuspješan upload loga.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleLogoDelete() {
    try {
      setDeleting(true);
      const res = await fetch(`${API}/logo`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Greška brisanja.');
      setLogoUrl(null);
      setError('');
      setSuccess('Logo je obrisan.');
      
      // Notify navbar to refresh logo
      window.dispatchEvent(new CustomEvent('companyLogoUpdated'));
    } catch (e) {
      setSuccess('');
      setError('Neuspješno brisanje loga.');
    } finally {
      setDeleting(false);
    }
  }

  async function handleNavbarLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('navbar_logo', file);
    try {
      setUploading(true);
      const res = await fetch(`${API}/navbar-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      if (!res.ok) throw new Error('Greška uploada.');
      const data = await res.json();
      setNavbarLogoUrl(data.navbar_logo_url);
      setError('');
      setSuccess('Navbar logo je uspješno ažuriran.');
      
      // Notify navbar to refresh logo
      window.dispatchEvent(new CustomEvent('navbarLogoUpdated'));
    } catch (e) {
      setSuccess('');
      setError('Neuspješan upload navbar loga.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleNavbarLogoDelete() {
    try {
      setDeleting(true);
      const res = await fetch(`${API}/navbar-logo`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Greška brisanja.');
      setNavbarLogoUrl(null);
      setError('');
      setSuccess('Navbar logo je obrisan.');
      
      // Notify navbar to refresh logo
      window.dispatchEvent(new CustomEvent('navbarLogoUpdated'));
    } catch (e) {
      setSuccess('');
      setError('Neuspješno brisanje navbar loga.');
    } finally {
      setDeleting(false);
    }
  }

  async function handleCertificateChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('certificate', file);
    try {
      setUploading(true);
      const res = await fetch(`${API}/certificate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      if (!res.ok) throw new Error('Greška uploada.');
      const data = await res.json();
      setCertificateUrl(data.certificate_url);
      setError('');
      setSuccess('Certifikat je uspješno ažuriran.');
    } catch (e) {
      setSuccess('');
      setError('Neuspješan upload certifikata.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleCertificateDelete() {
    try {
      setDeleting(true);
      const res = await fetch(`${API}/certificate`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Greška brisanja.');
      setCertificateUrl(null);
      setError('');
      setSuccess('Certifikat je obrisan.');
    } catch (e) {
      setSuccess('');
      setError('Neuspješno brisanje certifikata.');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return <div>Učitavanje...</div>;

  return (
    <div className="company-info max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">O firmi</h1>
      {success && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 text-green-800 px-3 py-2" role="status" aria-live="polite">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2" role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Naziv firme</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 block w-full border rounded-md p-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Opis firme</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 block w-full border rounded-md p-2 h-32" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Footer za PDF izvještaje</label>
          <textarea value={pdfFooter} onChange={(e) => setPdfFooter(e.target.value)} className="mt-1 block w-full border rounded-md p-2 h-24" placeholder="Npr. Naziv • Adresa • Telefon | Email" />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="registrationEnabled"
            type="checkbox"
            checked={registrationEnabled}
            onChange={(e) => setRegistrationEnabled(e.target.checked)}
          />
          <label htmlFor="registrationEnabled" className="text-sm text-gray-800">
            Dozvoli samo-registraciju servisera (prikaži "Registracija" i omogući prijave)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="workOrderEditEnabled"
            type="checkbox"
            checked={workOrderEditEnabled}
            onChange={(e) => setWorkOrderEditEnabled(e.target.checked)}
          />
          <label htmlFor="workOrderEditEnabled" className="text-sm text-gray-800">
            Dozvoli serviserima da edituju sve naloge za rad (ne samo svoje vlastite)
          </label>
        </div>
        <div className="flex items-center space-x-4">
          <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-60">
            {saving ? 'Spremam...' : 'Spremi'}
          </button>
        </div>
      </form>

      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">Logo firme (za PDF izvještaje)</label>
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="h-24 object-contain border rounded p-2 bg-white" />
        ) : (
          <div className="text-gray-500">Nema loga</div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <label
            htmlFor="logo-input"
            className={`inline-flex items-center justify-center px-4 py-2 rounded-md border text-sm font-medium shadow-sm transition-colors ${
              uploading
                ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {uploading ? 'Učitavam...' : 'Odaberi logo'}
          </label>
          <input
            id="logo-input"
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            disabled={uploading}
            className="hidden"
          />
          {logoUrl && (
            <button
              type="button"
              onClick={handleLogoDelete}
              disabled={deleting}
              className="px-3 py-2 rounded-md border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60"
            >
              {deleting ? 'Brišem...' : 'Obriši logo'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">Navbar logo (za navigaciju)</label>
        {navbarLogoUrl ? (
          <img src={navbarLogoUrl} alt="Navbar Logo" className="h-24 object-contain border rounded p-2 bg-white" />
        ) : (
          <div className="text-gray-500">Nema navbar loga</div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <label
            htmlFor="navbar-logo-input"
            className={`inline-flex items-center justify-center px-4 py-2 rounded-md border text-sm font-medium shadow-sm transition-colors ${
              uploading
                ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {uploading ? 'Učitavam...' : 'Odaberi navbar logo'}
          </label>
          <input
            id="navbar-logo-input"
            type="file"
            accept="image/*"
            onChange={handleNavbarLogoChange}
            disabled={uploading}
            className="hidden"
          />
          {navbarLogoUrl && (
            <button
              type="button"
              onClick={handleNavbarLogoDelete}
              disabled={deleting}
              className="px-3 py-2 rounded-md border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60"
            >
              {deleting ? 'Brišem...' : 'Obriši navbar logo'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">Certifikat firme</label>
        {certificateUrl ? (
          <img src={certificateUrl} alt="Certifikat" className="h-24 object-contain border rounded p-2 bg-white" />
        ) : (
          <div className="text-gray-500">Nema certifikata</div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <label
            htmlFor="certificate-input"
            className={`inline-flex items-center justify-center px-4 py-2 rounded-md border text-sm font-medium shadow-sm transition-colors ${
              uploading
                ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {uploading ? 'Učitavam...' : 'Odaberi certifikat'}
          </label>
          <input
            id="certificate-input"
            type="file"
            accept="image/*"
            onChange={handleCertificateChange}
            disabled={uploading}
            className="hidden"
          />
          {certificateUrl && (
            <button
              type="button"
              onClick={handleCertificateDelete}
              disabled={deleting}
              className="px-3 py-2 rounded-md border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60"
            >
              {deleting ? 'Brišem...' : 'Obriši certifikat'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
